/** Non-component map helpers shared by MapPage and SpotMapPicker. */
import { useEffect, type CSSProperties, type MutableRefObject } from "react";
import L from "leaflet";

import { PLACE_COLOR } from "@/lib/eventColors";
import { getTelegramWebApp } from "@/lib/telegram";
import type { PlaceType } from "@/types/api";

export const MOSCOW: L.LatLngTuple = [55.7558, 37.6176];

export function makeIcon(color: string, live = false): L.DivIcon {
  return L.divIcon({
    className: "sp-marker" + (live ? " sp-marker--live" : ""),
    iconSize: [24, 32],
    iconAnchor: [12, 32],
    html: `
      <svg width="24" height="32" viewBox="0 0 24 32" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 0C5.4 0 0 5.3 0 11.8 0 20 12 32 12 32s12-12 12-20.2C24 5.3 18.6 0 12 0z" fill="${color}" />
        <circle cx="12" cy="12" r="5" fill="#fff" />
      </svg>`,
  });
}

// Build one DivIcon per place type at module scope — Leaflet re-renders
// markers whenever the icon identity changes, so we keep a stable reference.
export const MARKER_ICONS: Record<PlaceType, L.DivIcon> = {
  outdoor_spot: makeIcon(PLACE_COLOR.outdoor_spot),
  indoor_hall: makeIcon(PLACE_COLOR.indoor_hall),
  store: makeIcon(PLACE_COLOR.store),
};

// Live variant: uses brand-orange regardless of place type so a running
// tournament reads as the same kind of "now" highlight everywhere.
export const LIVE_MARKER_ICON: L.DivIcon = makeIcon(PLACE_COLOR.outdoor_spot, true);

/**
 * iOS Telegram WebView often mounts the map before its container has a
 * stable height: `100dvh` resolves while the URL bar is still collapsing,
 * and TG itself swaps `viewportHeight` after `expand()` lands. Leaflet
 * caches container size on creation, so without a re-measure tiles
 * never get fetched and the page stays grey.
 *
 * Two nudges:
 *   - a couple of staggered `invalidateSize` calls after mount, to
 *     catch whichever frame the WebView settles on;
 *   - subscribe to TG's `viewportChanged` event for the same reason.
 */
export function useInvalidateOnViewport(mapRef: MutableRefObject<L.Map | null>) {
  useEffect(() => {
    const invalidate = () => mapRef.current?.invalidateSize();
    const t1 = window.setTimeout(invalidate, 100);
    const t2 = window.setTimeout(invalidate, 500);
    const t3 = window.setTimeout(invalidate, 1500);

    const tg = getTelegramWebApp();
    if (tg) tg.onEvent("viewportChanged", invalidate);

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
      if (tg) tg.offEvent("viewportChanged", invalidate);
    };
  }, [mapRef]);
}

export const MAP_ROUND_BUTTON: CSSProperties = {
  width: 48,
  height: 48,
  borderRadius: "50%",
  border: "none",
  background: "var(--bg-section)",
  color: "var(--fg1)",
  boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
};

export interface SearchableSpot {
  id: number;
  name: string;
  address: string;
}

/** Case-insensitive name/address match, capped at 8 hits. */
export function searchSpots<T extends SearchableSpot>(spots: T[], query: string): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return spots
    .filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        s.address.toLowerCase().includes(q),
    )
    .slice(0, 8);
}
